// src/pages/RoleList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaFilter,
  FaTimesCircle,
  FaPlus,
  FaPowerOff,
  FaUserShield,
} from "react-icons/fa";
import { FiRotateCcw } from "react-icons/fi";
import toast from "react-hot-toast";

import {
  useGetAllRolesQuery,
  useActivateRoleMutation,
  useDeactivateRoleMutation,
  useDeleteRoleMutation,
} from "../toolkitServices/roleApi";
import { useGetAllCompaniesQuery } from "../toolkitServices/companyApi";
import { useDebounce } from "../hooks/useDebounced";
import { useSelector } from "react-redux";

/* ------------ Tiny reusable spinner (SalesList style) ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);
const canDelete = (user) => {
  const perm = user?.permissions?.find((p) => p.moduleKey === "quotation");
  return user?.role === "owner" || perm?.permissions?.delete;
};
const RoleList = () => {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const { user } = useSelector((state) => state.auth);

  // search + debounce
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 350);

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // filters (applied)
  const [filters, setFilters] = useState({
    companyId: "",
    status: "", // "", "Active", "Inactive"
  });

  // filter modal
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(filters);

  const openFilters = () => {
    setTempFilters(filters);
    setIsFilterOpen(true);
  };

  // status -> boolean
  const activeBool = useMemo(() => {
    if (filters.status === "Active") return true;
    if (filters.status === "Inactive") return false;
    return undefined;
  }, [filters.status]);

  // companies for dropdown
  const { data: companiesResp, isLoading: isCompanyLoading } =
    useGetAllCompaniesQuery(
      { page: 1, limit: 1000, search: "" },
      { refetchOnMountOrArgChange: false }
    );

  const companies = useMemo(() => companiesResp?.data || [], [companiesResp?.data]);

  const selectedCompanyObj = useMemo(() => {
    if (!filters.companyId) return null;
    return companies.find((c) => c?._id === filters.companyId) || null;
  }, [companies, filters.companyId]);

  // query params
  const qParams = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || "",
      company: filters.companyId || undefined,
      active: activeBool,
    }),
    [page, limit, debouncedSearch, filters.companyId, activeBool]
  );

  const { data, isLoading, isFetching, error } = useGetAllRolesQuery(qParams, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const [activateRole] = useActivateRoleMutation();
  const [deactivateRole] = useDeactivateRoleMutation();
  const [deleteRole] = useDeleteRoleMutation();

  const rows = useMemo(() => data?.data || [], [data?.data]);
  const total = data?.total ?? 0;
  const pages = data?.pages ?? Math.max(1, Math.ceil((total || 0) / limit));
  const isDeleteAllowed = canDelete(user);

  // row loaders
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // toggle active
  const onToggleActive = async (row) => {
    if (!row?._id) return;
    try {
      setTogglingId(row._id);
      if (row.isActive) {
        await deactivateRole(row._id).unwrap();
        toast.success("Role deactivated");
      } else {
        await activateRole(row._id).unwrap();
        toast.success("Role activated");
      }
    } catch (e) {
      toast.error(e?.data?.message || e?.data?.msg || "Action failed");
      console.error(e);
    } finally {
      setTogglingId(null);
    }
  };

  // delete
  const handleDelete = async (id, name) => {
    if (!id) return;
    const ok = window.confirm(`Delete "${name || "this role"}"?`);
    if (!ok) return;

    try {
      setDeletingId(id);
      await deleteRole(id).unwrap();
      toast.success("Role deleted successfully");
    } catch (e) {
      toast.error(e?.data?.message || e?.data?.msg || "Failed to delete role");
      console.error(e);
    } finally {
      setDeletingId(null);
    }
  };

  // go page 1 when filters/search change
  useEffect(() => {
    setPage(1);
  }, [filters.companyId, filters.status, debouncedSearch]);

  // keyboard shortcuts: Ctrl+P create, Ctrl+F focus search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && (e.key === "p" || e.key === "P")) {
        e.preventDefault();
        navigate("/organisation/role-master-list/new-role");
      }
      if (e.ctrlKey && (e.key === "f" || e.key === "F")) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  // chips
  const chips = useMemo(() => {
    const arr = [];
    if (search?.trim()) arr.push({ key: "search", label: `Search: ${search.trim()}` });
    if (selectedCompanyObj?.companyName)
      arr.push({ key: "company", label: `Company: ${selectedCompanyObj.companyName}` });
    if (filters.status) arr.push({ key: "status", label: `Status: ${filters.status}` });
    return arr;
  }, [search, selectedCompanyObj, filters.status]);

  const clearChip = (key) => {
    if (key === "search") setSearch("");
    if (key === "company") setFilters((f) => ({ ...f, companyId: "" }));
    if (key === "status") setFilters((f) => ({ ...f, status: "" }));
  };

  const resetFilters = () => {
    setSearch("");
    setFilters({ companyId: "", status: "" });
  };

  const paginationSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 32,
      height: 32,
      borderRadius: 6,
      borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
      boxShadow: state.isFocused
        ? "0 0 0 1px rgba(148,129,206,.25)"
        : "none",
      fontSize: 10,
    }),
    valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
    menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
    option: (base, state) => ({
      ...base,
      padding: "6px 8px",
      backgroundColor: state.isSelected
        ? "#9481ce"
        : state.isFocused
          ? "#ede9fe"
          : "white",
      color: state.isSelected ? "white" : "#334155",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-2xl bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* Global overlay loader */}
      {isLoading && !rows.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-slate-600 text-xs sm:text-sm">
            <Spinner size={22} />
            <span>Loading roles…</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <h2 className="flex min-w-0 items-center gap-2 truncate text-[15px] font-semibold tracking-tight sm:text-lg">
            <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-xl bg-white/15 text-xs">
              <FaUserShield />
            </span>
            <span className="truncate">
              Roles {isFetching ? "…" : `(${total})`}
            </span>
          </h2>

          {isFetching && rows.length > 0 && (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <span className="hidden lg:inline text-[11px] text-violet-100/90">
            Ctrl + P to create new
          </span>

          <Link
            to="/organisation/role-master-list/new-role"
            className="inline-flex h-9 items-center gap-2 rounded-xl bg-white px-3 text-[11px] font-semibold text-[#6b4fd8] shadow-sm transition hover:bg-violet-50 sm:rounded-full"
          >
            <FaPlus className="text-[10px]" />
            <span className="hidden sm:inline">Add New</span>
            <span className="sm:hidden">Add</span>
          </Link>
        </div>
      </div>

      {/* Filters bar */}
      <div className="shrink-0 border-b bg-slate-50/80 px-3 py-2 sm:px-4">
        {/* MOBILE */}
        <div className="flex items-center gap-2 sm:hidden">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search role / company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
          />
          <button
            onClick={openFilters}
            className="inline-flex h-9 flex-shrink-0 items-center gap-1 rounded-lg border border-violet-100 bg-white px-3 text-[11px] font-semibold text-[#6b59c6] shadow-sm hover:bg-violet-50"
            title="Filters"
          >
            <FaFilter /> Filters
          </button>
        </div>

        {/* DESKTOP */}
        <div className="hidden items-end gap-2 sm:flex">
          {/* Company */}
          <div className="flex w-[220px] flex-col gap-[1px]">
            <label className="text-[11px] font-medium text-slate-600">Company</label>
            <select
              value={filters.companyId}
              onChange={(e) => setFilters((f) => ({ ...f, companyId: e.target.value }))}
              className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
              disabled={isCompanyLoading}
            >
              <option value="">All companies</option>
              {companies.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.companyName}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div className="flex w-[140px] flex-col gap-[1px]">
            <label className="text-[11px] font-medium text-slate-600">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
            >
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {/* Search */}
          <div className="flex min-w-[220px] flex-1 flex-col gap-[1px]">
            <label className="text-[11px] font-medium text-slate-600">Search</label>
            <input
              ref={searchRef}
              type="text"
              placeholder="Role name / company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          {/* Filters button */}
          <div className="flex flex-col gap-[1px] w-auto">
            <label className="text-[11px] font-medium text-slate-600 invisible">
              Filters
            </label>
            <button
              onClick={openFilters}
              className="inline-flex h-9 items-center gap-1 rounded-lg border border-violet-100 bg-white px-3 text-[11px] font-semibold text-[#6b59c6] shadow-sm hover:bg-violet-50"
            >
              <FaFilter /> Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error strip */}
      {error && (
        <div className="shrink-0 border-b bg-red-50 px-3 py-1.5 text-xs text-red-600 sm:px-4">
          {error?.data?.message || "Failed to load roles"}
        </div>
      )}

      {/* Chips */}
      {chips.length > 0 && (
        <div className="flex shrink-0 items-center gap-2 border-b bg-slate-50 px-3 py-1.5 sm:px-4">
          <div className="flex min-w-0 flex-nowrap items-center gap-2 overflow-x-auto pr-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {chips.map((c) => (
              <span
                key={c.key}
                className="inline-flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full border bg-white px-2 py-1 text-[11px] shadow-sm"
              >
                {c.label}
                <button
                  className="text-slate-500 hover:text-red-500"
                  onClick={() => clearChip(c.key)}
                  title="Clear"
                >
                  <FaTimesCircle />
                </button>
              </span>
            ))}
          </div>

          <button
            className="ml-auto flex-shrink-0 text-[11px] text-[#6b59c6] hover:underline"
            onClick={resetFilters}
            title="Reset all"
          >
            Reset
          </button>
        </div>
      )}

      {/* Table */}
      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain bg-white [-webkit-overflow-scrolling:touch]">
        <table className="w-full min-w-[880px] text-left text-xs sm:text-sm">
<thead className="sticky top-0 z-40 bg-[#efeafd] text-[10px] uppercase text-[#4b3d91] shadow-[0_1px_0_rgba(148,129,206,.18)] sm:text-[11px]">
            <tr>
              <th className="px-3 py-2 sm:px-4">#</th>
              <th className="px-3 py-2 sm:px-4">Role Name</th>
              <th className="px-3 py-2 sm:px-4">Company</th>
              <th className="px-3 py-2 sm:px-4">Short Name</th>
              <th className="px-3 py-2 sm:px-4">Status</th>
<th className="sticky right-0 top-0 z-50 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] px-1 py-2 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px] sm:px-2">
                <span className="sm:hidden">Act</span>
                <span className="hidden sm:inline">Action</span>
              </th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            <AnimatePresence>
              {rows.length ? (
                rows.map((item, index) => {
                  const id = item?._id;
                  const isToggling = togglingId === id;
                  const isDeleting = deletingId === id;

                  return (
                    <motion.tr
                      key={id || index}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.18, delay: index * 0.01 }}
                      className={`group border-t border-slate-100 transition hover:bg-[#f7f5ff] ${
                        isDeleting ? "opacity-60" : ""
                      }`}
                    >
                      <td className="px-3 py-2.5 font-medium sm:px-4">
                        {(page - 1) * limit + index + 1}
                      </td>

                      <td className="max-w-[220px] px-3 py-2.5 sm:px-4">
                        <span className="block truncate" title={item?.roleName || ""}>
                          {item?.roleName || "—"}
                        </span>
                      </td>

                      <td className="max-w-[240px] px-3 py-2.5 sm:px-4">
                        <span
                          className="block truncate"
                          title={item?.company?.companyName || ""}
                        >
                          {item?.company?.companyName || "—"}
                        </span>
                      </td>

                      <td className="px-3 py-2.5 sm:px-4">
                        {item?.shortName || "—"}
                      </td>

                      <td className="px-3 py-2.5 sm:px-4">
                        <span
                          className={[
                            "text-[10px] sm:text-xs px-2 py-1 rounded-full",
                            item?.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700",
                          ].join(" ")}
                        >
                          {item?.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>

<td className="sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 px-1 py-2 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px] sm:px-2">
                        <div className="inline-flex items-center justify-center gap-0.5 sm:gap-1">
                        {/* Edit */}
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                          title="Edit"
                          aria-label="Edit role"
                          onClick={() =>
                            navigate(`/organisation/role-master-list/${id}/edit`)
                          }
                        >
                          <FaPencilAlt />
                        </motion.button>

                        {/* Activate/Deactivate */}
                        <button
                          className={[
                            "inline-flex h-6 w-6 items-center justify-center rounded-md border text-[10px] disabled:opacity-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]",
                            item?.isActive
                              ? "border-amber-100 text-amber-600 hover:bg-amber-50"
                              : "border-emerald-100 text-emerald-600 hover:bg-emerald-50",
                          ].join(" ")}
                          disabled={isToggling}
                          onClick={() => onToggleActive(item)}
                          title={item?.isActive ? "Deactivate" : "Activate"}
                          aria-label={item?.isActive ? "Deactivate role" : "Activate role"}
                        >
                          {isToggling ? <Spinner size={12} /> : <FaPowerOff />}
                        </button>

                        {/* Delete */}
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-rose-500 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                          title={isDeleteAllowed ? "Delete" : "No delete permission"}
                          aria-label="Delete role"
                          disabled={isDeleting || !isDeleteAllowed}
                          onClick={() => {
                            if (isDeleteAllowed) {
                              handleDelete(id, item?.roleName);
                            } else {
                              toast.error("You don't have permission to delete");
                            }
                          }}
                        >
                          {isDeleting ? <Spinner size={12} /> : <FaTrashAlt />}
                        </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              ) : !isLoading ? (
                <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <td colSpan="6" className="py-8 text-center text-gray-500">
                    No matching records found.
                  </td>
                </motion.tr>
              ) : null}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex shrink-0 items-center justify-between gap-2 rounded-b-2xl border-t bg-slate-50/60 px-2 py-1.5 text-[10px] sm:px-4 sm:py-2 sm:text-sm">
        <div className="hidden text-slate-600 sm:block">
          Page <span className="font-semibold">{page}</span> of{" "}
          <span className="font-semibold">{pages}</span> • {total ?? 0} record(s)
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <div className="flex shrink-0 items-center gap-1 text-slate-600">
            <span className="hidden text-xs sm:inline">Rows / page</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              onChange={(opt) => {
                setLimit(opt?.value || 10);
                setPage(1);
              }}
              options={[10, 20, 50, 100].map((value) => ({
                value,
                label: String(value),
              }))}
              styles={paginationSelectStyles}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

        <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none sm:justify-start sm:gap-2">
          <button
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-violet-50 disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || isFetching || isLoading}
            aria-label="Previous page"
          >
            <span className="sm:hidden" aria-hidden="true">
              &lsaquo;
            </span>
            <span className="hidden sm:inline">Prev</span>
          </button>
          <span className="flex items-center gap-1 text-slate-600 sm:hidden">
            <Select
              value={{
                value: pages ? page : 1,
                label: String(pages ? page : 1),
              }}
              options={Array.from({ length: Math.max(1, pages) }, (_, index) => ({
                value: index + 1,
                label: String(index + 1),
              }))}
              onChange={(option) => setPage(Number(option?.value) || 1)}
              isDisabled={isFetching || isLoading}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[52px]"
              aria-label="Select page number"
            />
            <span>/ {Math.max(1, pages)}</span>
          </span>
          <span className="hidden items-center gap-1 text-slate-600 sm:flex">
            <span>Page</span>
            <Select value={{ value: pages ? page : 1, label: String(pages ? page : 1) }} options={Array.from({ length: Math.max(1, pages) }, (_, index) => ({ value: index + 1, label: String(index + 1) }))} onChange={(option) => setPage(Number(option?.value) || 1)} isDisabled={isFetching || isLoading} isSearchable={false} menuPlacement="top" menuPosition="fixed" menuPortalTarget={document.body} menuShouldScrollIntoView={false} styles={paginationSelectStyles} className="w-[58px]" aria-label="Select page number" />
            <span>of {Math.max(1, pages)}</span>
          </span>
          <button
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-violet-50 disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages || isFetching || isLoading}
            aria-label="Next page"
          >
            <span className="sm:hidden" aria-hidden="true">
              &rsaquo;
            </span>
            <span className="hidden sm:inline">Next</span>
          </button>
        </div>
        </div>
      </div>

      {/* Filters Modal */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Advanced filters
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Filter roles by company and status.
                  </p>
                </div>
                <button
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setIsFilterOpen(false)}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 space-y-4 text-[11px] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Company */}
                  <div>
                    <label className="block text-slate-600 mb-1">Company</label>
                    <select
                      value={tempFilters.companyId}
                      onChange={(e) =>
                        setTempFilters((f) => ({ ...f, companyId: e.target.value }))
                      }
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                      disabled={isCompanyLoading}
                    >
                      <option value="">All companies</option>
                      {companies.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.companyName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-slate-600 mb-1">Status</label>
                    <select
                      value={tempFilters.status}
                      onChange={(e) =>
                        setTempFilters((f) => ({ ...f, status: e.target.value }))
                      }
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                    >
                      <option value="">All</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  {/* Search */}
                  <div className="sm:col-span-2">
                    <label className="block text-slate-600 mb-1">Search</label>
                    <input
                      type="text"
                      placeholder="Role / company…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                    />
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    resetFilters();
                    setTempFilters({ companyId: "", status: "" });
                  }}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFilters(tempFilters);
                      setPage(1);
                      setIsFilterOpen(false);
                    }}
                    className="px-3 py-1.5 rounded-md text-[11px] text-white bg-[#9481ce] hover:opacity-95"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RoleList;
